<script setup>
// Painel da esquerda: elementos (para colunas), estruturas e seções prontas
// (para linhas) e "Meus blocos" (linhas/elementos salvos pela conta).
// Arrastar usa clone; clicar insere perto da seleção.
import { computed, inject, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import { useAlert } from 'dashboard/composables';

const ed = inject('emailEditor');
const { t } = useI18n();
const tab = ref('elements');
// Só aparecem os tipos que o editor sabe desenhar (o catálogo pode chegar
// antes do componente do elemento novo).
const ICONS = {
  heading: 'i-lucide-heading',
  text: 'i-lucide-text',
  image: 'i-lucide-image',
  logo: 'i-lucide-badge',
  button: 'i-lucide-mouse-pointer-click',
  divider: 'i-lucide-minus',
  spacer: 'i-lucide-move-vertical',
  html: 'i-lucide-code',
  video: 'i-lucide-video',
  social: 'i-lucide-share-2',
  product: 'i-lucide-shopping-bag',
};

const elements = computed(() =>
  (ed.catalog.value?.layout?.element_types || [])
    .filter(type => ICONS[type])
    .map(type => ({ id: `palette-${type}`, __element: type }))
);
const structures = computed(() =>
  Object.entries(ed.catalog.value?.layout?.structures || {}).map(
    ([key, widths]) => ({ id: `structure-${key}`, key, __structure: widths })
  )
);
const sections = computed(() =>
  (ed.catalog.value?.sections || []).map(s => ({
    id: `section-${s.key}-${s.variant}`,
    label: s.key,
    variant: s.variant,
    __section: s,
  }))
);
const savedGroups = computed(() =>
  [
    { key: 'row', label: 'ROWS', dnd: 'rows' },
    { key: 'element', label: 'ELEMENTS', dnd: 'elements' },
  ].map(group => ({
    ...group,
    items: (ed.savedBlocks?.value || [])
      .filter(b => b.kind === group.key)
      .map(b => ({ id: `saved-${b.id}`, __saved: b })),
  }))
);
const cloneItem = item => ({ ...item });
const TABS = ['elements', 'sections', 'saved'];

const openTab = name => {
  tab.value = name;
  if (name === 'saved' && !ed.savedLoaded.value) ed.loadSavedBlocks();
};

const editingId = ref(null);
const draftName = ref('');
const confirmingId = ref(null);
const fail = error =>
  useAlert(
    error?.response?.data?.error || t('EMAIL_TEMPLATES.EDITOR.SAVED.ERROR')
  );
const CONFIRM_MS = 4000;
let confirmTimer = null;
const disarm = () => {
  clearTimeout(confirmTimer);
  confirmTimer = null;
  confirmingId.value = null;
};
const startRename = block => {
  disarm();
  editingId.value = block.id;
  draftName.value = block.name;
};
const finishRename = async block => {
  if (editingId.value !== block.id) return;
  editingId.value = null;
  const name = draftName.value.trim();
  if (!name || name === block.name) return;
  try {
    await ed.renameSaved(block.id, name);
  } catch (error) {
    fail(error);
  }
};
// Excluir pede um segundo clique (o ícone vira "confirmar"); a confirmação
// se desarma sozinha em 4 s ou ao mexer em outro bloco.
const removeBlock = async block => {
  if (confirmingId.value !== block.id) {
    disarm();
    confirmingId.value = block.id;
    confirmTimer = setTimeout(disarm, CONFIRM_MS);
    return;
  }
  disarm();
  try {
    await ed.deleteSaved(block.id);
  } catch (error) {
    fail(error);
  }
};
const insertBlock = block => {
  disarm();
  ed.insertSaved(block);
};
onBeforeUnmount(disarm);
</script>

<template>
  <div class="flex h-full flex-col">
    <div class="flex border-b border-n-weak">
      <button
        v-for="name in TABS"
        :key="name"
        :data-tab="name"
        type="button"
        class="flex-1 border-b-2 py-2 text-xs font-semibold"
        :class="
          tab === name
            ? 'border-n-brand text-n-brand'
            : 'border-transparent text-n-slate-10'
        "
        @click="openTab(name)"
      >
        {{ t(`EMAIL_TEMPLATES.EDITOR.ADD.${name.toUpperCase()}`) }}
      </button>
    </div>
    <p class="px-3 pt-3 text-[11px] text-n-slate-10">
      {{ t('EMAIL_TEMPLATES.EDITOR.ADD.HINT') }}
    </p>
    <div class="min-h-0 flex-1 overflow-y-auto p-3">
      <template v-if="tab === 'elements'">
        <Draggable
          :list="elements"
          item-key="id"
          :group="{ name: 'elements', pull: 'clone', put: false }"
          :clone="cloneItem"
          :sort="false"
          class="grid grid-cols-2 gap-2"
        >
          <template #item="{ element }">
            <button
              :data-add-element="element.__element"
              type="button"
              class="flex cursor-grab flex-col items-center gap-1.5 rounded-lg border border-n-weak bg-n-solid-1 py-3 text-xs text-n-slate-12 hover:border-n-brand hover:text-n-brand"
              @click="ed.addElement(element.__element)"
            >
              <span :class="ICONS[element.__element]" class="size-5" />
              {{ t(`EMAIL_TEMPLATES.EDITOR.ELEMENT.${element.__element}`) }}
            </button>
          </template>
        </Draggable>
        <p class="pb-2 pt-4 text-xs font-semibold text-n-slate-11">
          {{ t('EMAIL_TEMPLATES.EDITOR.ADD.STRUCTURES') }}
        </p>
        <Draggable
          :list="structures"
          item-key="id"
          :group="{ name: 'rows', pull: 'clone', put: false }"
          :clone="cloneItem"
          :sort="false"
          class="flex flex-col gap-2"
        >
          <template #item="{ element }">
            <button
              :data-add-structure="element.key"
              type="button"
              class="flex cursor-grab flex-col gap-1.5 rounded-lg border border-n-weak bg-n-solid-1 p-2 text-left text-xs text-n-slate-12 hover:border-n-brand"
              @click="ed.addStructure(element.__structure)"
            >
              <span class="flex h-6 gap-1">
                <span
                  v-for="(w, i) in element.__structure"
                  :key="i"
                  class="rounded bg-n-brand/20"
                  :style="{ flexBasis: `${w}%` }"
                />
              </span>
              {{ t(`EMAIL_TEMPLATES.EDITOR.STRUCTURE.${element.key}`) }}
            </button>
          </template>
        </Draggable>
      </template>
      <Draggable
        v-else-if="tab === 'sections'"
        :list="sections"
        item-key="id"
        :group="{ name: 'rows', pull: 'clone', put: false }"
        :clone="cloneItem"
        :sort="false"
        class="flex flex-col gap-2"
      >
        <template #item="{ element }">
          <button
            :data-add-section="`${element.label}:${element.variant}`"
            type="button"
            class="cursor-grab rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-left text-xs text-n-slate-12 hover:border-n-brand"
            @click="ed.addSection(element.__section)"
          >
            <span class="font-medium">{{
              t(`EMAIL_TEMPLATES.BUILDER.BLOCKS.${element.label}`)
            }}</span>
            <span class="ml-1 text-n-slate-10">
              {{ t(`EMAIL_TEMPLATES.BUILDER.VARIANTS.${element.variant}`) }}
            </span>
          </button>
        </template>
      </Draggable>
      <template v-else>
        <p
          v-if="ed.savedLoaded.value && !(ed.savedBlocks.value || []).length"
          class="text-xs leading-snug text-n-slate-10"
        >
          {{ t('EMAIL_TEMPLATES.EDITOR.SAVED.EMPTY') }}
        </p>
        <template v-for="group in savedGroups" :key="group.key">
          <p
            v-if="group.items.length"
            class="pb-2 pt-1 text-xs font-semibold text-n-slate-11"
          >
            {{ t(`EMAIL_TEMPLATES.EDITOR.SAVED.${group.label}`) }}
          </p>
          <Draggable
            :list="group.items"
            item-key="id"
            :group="{ name: group.dnd, pull: 'clone', put: false }"
            :clone="cloneItem"
            :sort="false"
            class="mb-3 flex flex-col gap-2"
          >
            <template #item="{ element }">
              <div
                :data-saved="element.__saved.id"
                class="flex cursor-grab items-center gap-2 rounded-lg border border-n-weak bg-n-solid-1 px-2 py-2 text-xs text-n-slate-12 hover:border-n-brand"
              >
                <input
                  v-if="editingId === element.__saved.id"
                  v-model="draftName"
                  data-test="rename-input"
                  maxlength="80"
                  class="min-w-0 flex-1 rounded border border-n-weak bg-n-solid-2 px-1.5 py-0.5 text-xs"
                  @keydown.enter.prevent="finishRename(element.__saved)"
                  @blur="finishRename(element.__saved)"
                />
                <button
                  v-else
                  type="button"
                  data-test="insert"
                  class="min-w-0 flex-1 truncate text-left font-medium"
                  @click="insertBlock(element.__saved)"
                >
                  {{ element.__saved.name }}
                </button>
                <button
                  type="button"
                  data-test="rename"
                  class="i-lucide-pencil size-3.5 shrink-0 text-n-slate-10 hover:text-n-slate-12"
                  :title="t('EMAIL_TEMPLATES.EDITOR.SAVED.RENAME')"
                  @click.stop="startRename(element.__saved)"
                />
                <button
                  type="button"
                  data-test="delete"
                  class="size-3.5 shrink-0"
                  :class="
                    confirmingId === element.__saved.id
                      ? 'i-lucide-check text-n-ruby-11'
                      : 'i-lucide-trash-2 text-n-slate-10 hover:text-n-ruby-11'
                  "
                  :title="
                    confirmingId === element.__saved.id
                      ? t('EMAIL_TEMPLATES.EDITOR.SAVED.CONFIRM_DELETE')
                      : t('EMAIL_TEMPLATES.EDITOR.SAVED.DELETE')
                  "
                  @click.stop="removeBlock(element.__saved)"
                />
              </div>
            </template>
          </Draggable>
        </template>
      </template>
    </div>
  </div>
</template>
